import React from 'react';
import { motion } from 'framer-motion';
import SEOHead from '../components/SEOHead';
import { 
  Smartphone, 
  Zap, 
  Palette, 
  Database, 
  CheckCircle, 
  ArrowRight,
  Code,
  Users,
  Settings,
  Globe,
  Shield,
  Clock,
  Target,
  Layers,
  Play,
  Monitor,
  Tablet,
  MousePointer,
  Eye,
  RefreshCw,
  Link,
  BarChart3,
  FileText,
  Camera,
  Mic,
  MapPin
} from 'lucide-react';

const PowerAppsCanvasApp: React.FC = () => {
  const canvasAppFeatures = [
    {
      icon: <MousePointer className="w-6 h-6" />,
      title: "Drag-and-Drop Interface",
      description: "Build apps visually with an intuitive drag-and-drop designer that requires no coding experience.",
      features: ["Visual app builder", "Pre-built controls", "Custom layouts", "Responsive design"]
    },
    {
      icon: <Database className="w-6 h-6" />,
      title: "Data Connectivity",
      description: "Connect to 500+ data sources including SharePoint, SQL Server, Excel, and cloud services.",
      features: ["Multiple data sources", "Real-time sync", "Offline capabilities", "Data validation"]
    },
    {
      icon: <Smartphone className="w-6 h-6" />,
      title: "Cross-Platform Apps",
      description: "Create apps that work seamlessly across web browsers, tablets, and mobile devices.",
      features: ["Web responsive", "Mobile optimized", "Tablet friendly", "Cross-browser support"]
    },
    {
      icon: <Palette className="w-6 h-6" />,
      title: "Custom UI/UX",
      description: "Design beautiful, branded interfaces with custom themes, colors, and interactive controls.",
      features: ["Custom themes", "Brand integration", "Interactive controls", "Modern UI components"]
    }
  ];

  const appTypes = [
    {
      title: "Business Process Apps",
      description: "Streamline workflows like expense reporting, time tracking, and approval processes.",
      icon: <FileText className="w-8 h-8" />,
      examples: ["Expense Management", "Leave Requests", "Asset Tracking", "Inventory Management"]
    },
    {
      title: "Data Collection Apps",
      description: "Gather information through forms, surveys, and field data collection applications.",
      icon: <Database className="w-8 h-8" />,
      examples: ["Customer Surveys", "Inspection Forms", "Field Reports", "Event Registration"]
    },
    {
      title: "Dashboard & Analytics",
      description: "Create interactive dashboards and reporting applications for data visualization.",
      icon: <BarChart3 className="w-8 h-8" />,
      examples: ["Sales Dashboards", "KPI Tracking", "Performance Reports", "Executive Summaries"]
    },
    {
      title: "Communication Apps",
      description: "Build apps for team collaboration, announcements, and internal communication.",
      icon: <Users className="w-8 h-8" />,
      examples: ["Team Directory", "Announcement Board", "Meeting Scheduler", "Project Updates"]
    },
    {
      title: "Field Service Apps",
      description: "Empower field workers with mobile apps for service delivery and customer interaction.",
      icon: <MapPin className="w-8 h-8" />,
      examples: ["Work Order Management", "Customer Check-ins", "Photo Documentation", "GPS Tracking"]
    },
    {
      title: "Employee Productivity",
      description: "Enhance productivity with apps for task management, resource booking, and collaboration.",
      icon: <Target className="w-8 h-8" />,
      examples: ["Room Booking", "Equipment Checkout", "Task Management", "Skills Matrix"]
    }
  ];

  const benefits = [
    "Reduce app development time by 90%",
    "Lower development costs significantly",
    "No coding skills required",
    "Rapid prototyping and deployment",
    "Enterprise-grade security and compliance",
    "Seamless Microsoft 365 integration"
  ];

  const developmentProcess = [
    {
      step: "01",
      title: "Requirements Analysis",
      description: "Understand your business needs, user requirements, and define app functionality and scope.",
      color: "bg-blue-500"
    },
    {
      step: "02",
      title: "Data Source Planning",
      description: "Identify and configure data connections, establish data relationships, and plan data flow.",
      color: "bg-green-500"
    },
    {
      step: "03",
      title: "UI/UX Design",
      description: "Create wireframes, design user interfaces, and establish user experience workflows.",
      color: "bg-purple-500"
    },
    {
      step: "04",
      title: "App Development",
      description: "Build the app using Power Apps Studio with drag-and-drop controls and custom formulas.",
      color: "bg-orange-500"
    },
    {
      step: "05",
      title: "Testing & Refinement",
      description: "Test functionality across devices, gather user feedback, and refine the app experience.",
      color: "bg-red-500"
    },
    {
      step: "06",
      title: "Deployment & Training",
      description: "Deploy the app to users, provide training, and establish ongoing support processes.",
      color: "bg-indigo-500"
    }
  ];

  const advancedFeatures = [
    "AI Builder integration for intelligent features",
    "Power Automate workflow integration",
    "Custom connectors for unique data sources",
    "Advanced formulas and business logic",
    "Offline data synchronization",
    "Role-based security and permissions",
    "Multi-language support",
    "Custom components and PCF controls"
  ];

  const deviceCapabilities = [
    {
      icon: <Camera className="w-6 h-6" />,
      title: "Camera Integration",
      description: "Capture photos and scan barcodes directly within your apps"
    },
    {
      icon: <Mic className="w-6 h-6" />,
      title: "Audio Recording",
      description: "Record voice notes and audio for documentation purposes"
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "GPS Location",
      description: "Access device location for mapping and location-based features"
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Offline Capabilities",
      description: "Work offline and sync data when connectivity is restored"
    }
  ];

  return (
    <>
      <SEOHead
        title="Power Apps Canvas App Development | CRMONCE - No-Code App Solutions"
        description="Create custom business applications with Microsoft Power Apps Canvas Apps. Expert no-code development, cross-platform apps, and rapid deployment. Build apps 90% faster with CRMONCE."
        keywords="Power Apps Canvas App, no-code development, business applications, Microsoft Power Platform, app development, drag-and-drop app builder, mobile apps, custom applications, low-code platform, citizen developer, business process apps"
        canonical="https://crmonce.com/power-apps-canvas-app"
        ogImage="/images/power-apps-canvas-og.jpg"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Microsoft Power Apps Canvas App Development",
          "description": "Professional Microsoft Power Apps Canvas App development and no-code application solutions. Build apps 90% faster with drag-and-drop interface and cross-platform compatibility.",
          "provider": {
            "@type": "Organization",
            "name": "CRMONCE",
            "url": "https://crmonce.com",
            "logo": "https://crmonce.com/logo.png",
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+91-8096556344",
              "contactType": "sales",
              "email": "info@crmonce.com"
            }
          },
          "serviceType": "No-Code App Development",
          "category": "Software Development",
          "areaServed": {
            "@type": "Place",
            "name": "Worldwide"
          },
          "offers": {
            "@type": "Offer",
            "description": "Professional Power Apps Canvas App development and consulting services",
            "category": "App Development Services"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Canvas App Development Services",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Business Process Apps",
                  "description": "Streamline workflows like expense reporting and approval processes"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Data Collection Apps",
                  "description": "Gather information through forms, surveys, and field data collection"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Dashboard & Analytics Apps",
                  "description": "Create interactive dashboards and reporting applications"
                }
              }
            ]
          }
        }}
      />

      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
        {/* Hero Section */}
        <section className="pt-48 pb-20 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center container-custom"
            >
              <div className="flex items-center justify-center mb-6">
                <Smartphone className="w-12 h-12 mr-4" />
                <h1 className="text-5xl font-bold">Power Apps Canvas Development</h1>
              </div>
              <p className="text-xl text-purple-100 mb-8">
                Transform your business processes with custom no-code applications. Build powerful, 
                responsive apps that work across all devices without writing a single line of code.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-purple-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-purple-50 transition-all duration-300"
              >
                Start Building Your App
                <ArrowRight className="ml-2 w-5 h-5 inline" />
              </motion.button>
            </motion.div>
          </div>
        </section>

        {/* Canvas App Features */}
        <section className="py-20  bg-white">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Canvas App Capabilities
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Power Apps Canvas Apps provide unlimited flexibility to create exactly 
                the app your business needs with intuitive design tools.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {canvasAppFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-8 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center text-white mr-4">
                      {feature.icon}
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">{feature.title}</h3>
                  </div>
                  <p className="text-gray-600 mb-6">{feature.description}</p>
                  <div className="grid grid-cols-2 gap-3">
                    {feature.features.map((item, itemIndex) => (
                      <div key={itemIndex} className="flex items-center">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                        <span className="text-sm text-gray-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* App Types */}
        <section className="py-20 ">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Types of Canvas Apps We Build
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                From simple data collection forms to complex business applications, 
                we create Canvas Apps for every business need.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {appTypes.map((type, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow"
                >
                  <div className="text-purple-600 mb-6">
                    {type.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{type.title}</h3>
                  <p className="text-gray-600 mb-6">{type.description}</p>
                  <div className="space-y-2">
                    <h4 className="font-semibold text-gray-800">Examples:</h4>
                    {type.examples.map((example, exampleIndex) => (
                      <div key={exampleIndex} className="flex items-center">
                        <div className="w-2 h-2 bg-purple-400 rounded-full mr-3"></div>
                        <span className="text-sm text-gray-600">{example}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Development Process */}
        <section className="py-20  bg-white">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Our Development Process
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We follow a proven methodology to deliver Canvas Apps that meet your 
                business requirements and exceed user expectations.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {developmentProcess.map((process, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow relative"
                >
                  <div className={`absolute -top-4 -left-4 w-12 h-12 ${process.color} rounded-full flex items-center justify-center text-white font-bold text-lg`}>
                    {process.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 mt-4">{process.title}</h3>
                  <p className="text-gray-600">{process.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Device Capabilities */}
        <section className="py-20 ">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Native Device Integration
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Leverage device capabilities to create rich, interactive experiences 
                that work seamlessly across all platforms.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {deviceCapabilities.map((capability, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow text-center"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                    <div className="text-purple-600">
                      {capability.icon}
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{capability.title}</h3>
                  <p className="text-gray-600 text-sm">{capability.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20  bg-gradient-to-r from-purple-600 to-blue-600">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-white mb-6">
                Why Choose Canvas Apps?
              </h2>
              <p className="text-xl text-purple-100 max-w-3xl mx-auto">
                Experience the power of no-code development with enterprise-grade security and scalability.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className="flex items-center bg-white bg-opacity-10 rounded-lg p-6"
                >
                  <CheckCircle className="w-6 h-6 text-green-400 mr-4 flex-shrink-0" />
                  <span className="text-white font-medium">{benefit}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Advanced Features */}
        <section className="py-20  bg-white">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Advanced Features & Integrations
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Extend your Canvas Apps with advanced capabilities and seamless integrations 
                for enterprise-grade solutions.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {advancedFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className="flex items-center bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-6"
                >
                  <Zap className="w-6 h-6 text-purple-600 mr-4 flex-shrink-0" />
                  <span className="text-gray-800 font-medium">{feature}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20  bg-gradient-to-r from-gray-900 to-purple-900">
          <div className="container-custom text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl font-bold text-white mb-6">
                Ready to Build Your Canvas App?
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                Let our Power Apps experts help you create custom applications that 
                transform your business processes and empower your users.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors"
                >
                  Start Your App Project
                  <ArrowRight className="w-5 h-5 ml-2 inline" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors"
                >
                  Schedule Consultation
                </motion.button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PowerAppsCanvasApp;
